import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileImport, FaFileExcel, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import SelectComponent from "../../../../components/select/select-component.jsx";
import placeService from "../../../../services/web/place-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {exportObjectsToXlsx} from "../../../../utils/excel-export-utils.js";
import PlaceImport from "./components/place-import.jsx";

const initialFilter = {
    name: "",
    neighborhood: "",
    city: "",
    street: "",
    status: ""
}

const statusList = [
    {value: "true", label: "Ativo"},
    {value: "false", label: "Inativo"},
]

const PlaceList = () => {
    const navigate = navigateHook();

    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState(initialFilter)
    const [filter, setFilter] = useState({});
    const [place, setPlace] = useState([]);
    const [importOpen, setImportOpen] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const gridRef = useRef(null);
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const { status, refetch } = useQuery({
        queryFn: () =>  placeService.list(filter).then(({data}) => {
            setPlace(data.data)
            return []
        }),
        queryKey: ['place', filter]
    });

    useEffect(() => {
        if (status === 'success') setIsLoading(false)
        else setIsLoading(true)
    }, [status]);

    const columnDefs = [
        {headerName: "Local", field: "name", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        {headerName: "Bairro", field: "neighborhood", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        {headerName: "Município - UF", field: "city", flex: 1,
            cellRenderer: ({data}) => {return  data.city + " - " + data.state},
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        {headerName: "Logradouro", field: "street", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
    ];
    const onChange = ({target}) => {
        let {id, value} = target;

        setState(prev => ({...prev, [id]: value}));
    }

    const handleSelect = (id, value) => setState(prev => ({...prev, [id]: value}));


    const apply = useCallback(() => {
        gridRef.current?.api?.showLoadingOverlay();
        setFilter({
            name: state.name ,
            neighborhood: state.neighborhood ,
            city: state.city ,
            street: state.street ,
            status: state.status
        })

    }, [state]);

    const clearFilter = () => {
        setState(initialFilter);
        setFilter({});
    }

    const onCellClicked = ({data, event}) => {
        let urlDev = `/place/form/${data.id}`;
        navigate(urlDev, event.ctrlKey);
    }

    const newPlace = () => {
        let isBlank = false;
        let urlDev = `/place/form`;
        navigate(urlDev, isBlank);
    }

    const exportPlaces = async () => {
        if (!place.length) return;

        const places = [...place].sort((a, b) => a.id - b.id).map(item => ({
            "ID": item.id,
            "Nome": item.name,
            "Tipo": item.type,
            "Logradouro": item.street,
            "Número": item.number,
            "Bairro": item.neighborhood,
            "Complemento": item.complement,
            "CEP": item.zipcode,
            "Município": item.city,
            "UF": item.state,
            "CNPJ": item.cnpj,
            "Contato": item.contact,
            "Telefone": item.phone,
            "Latitude": item.latitude,
            "Longitude": item.longitude,
            "Ativo": item.status ? "Sim" : "Não"
        }));

        await exportObjectsToXlsx({
            fileName: "locais-pre-cadastrados.xlsx",
            sheetName: "Locais pré-cadastrados",
            data: places
        });
    }

    useGlobalKey("Enter", apply)
    useGlobalKey("Escape", clearFilter)
    return (
        <>
            <Template isLoading={isLoading || isImporting}>
            <Template.Header>
                <Template.Title>
                    Locais pré-cadastrados
                </Template.Title>
                <div className="flex gap-2">
                    <Button target="success" onClick={() => setImportOpen(true)}>
                        <FaFileImport/> Importar
                    </Button>
                    <Button target="success" onClick={exportPlaces}>
                        <FaFileExcel/>
                        Exportar
                    </Button>
                    <Button onClick={() => newPlace()}>
                        <FaPlus/>
                        Adicionar local
                    </Button>
                </div>
            </Template.Header>

            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-2">
                                <InputForm id="name" value={state.name} onChange={onChange}
                                           label="Nome"/>
                            </div>
                            <div className="col-span-2">
                                <InputForm id="neighborhood" value={state.neighborhood} onChange={onChange}
                                           label="Bairro"/>
                            </div>
                            <div className="col-span-2">
                                <InputForm id="city" value={state.city} onChange={onChange}
                                           label="Município - UF"/>
                            </div>
                            <div className="col-span-2">
                                <SelectComponent
                                    name="status"
                                    value={statusList.find(s => s.value === state?.status) ?? ""}
                                    options={statusList}
                                    label={"Status"}
                                    onChange={(selected) => handleSelect("status", selected?.value)}
                                />
                            </div>
                            <div className="flex col-span-4 gap-4">
                                <InputForm classNameWrapper={"w-full"} id="street" value={state.street} onChange={onChange}
                                           label="Logradouro"/>
                                <div className={"flex gap-2"}>
                                    <Button title="Limpar" onClick={() => clearFilter()} target={"primary-outline"}>
                                        <FaEraser />
                                    </Button>
                                    <Button title="Buscar" onClick={() => apply(state)}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-153px)] w-full aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={place}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={aggridUtils.defaultGridOptions()}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
            </Template>
            <PlaceImport open={importOpen} setOpen={setImportOpen} refetch={refetch} setIsLoading={setIsImporting}/>
        </>
    );
};

export default PlaceList;